/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { TESTIDS } from "@/constants/testIds";
import { Plus, X, Trash2 } from "lucide-react";
import ColumnChooser from "@/components/ColumnChooser";
import { useColumnPrefs } from "@/hooks/useColumnPrefs";

const ROLES = ["admin", "user", "warehouse", "reseller", "admin_reseller", "suspended"];

function roleBadge(role) {
  switch (role) {
    case "admin": return "es-badge es-badge-brand";
    case "warehouse": return "es-badge es-badge-info";
    case "reseller":
    case "admin_reseller": return "es-badge es-badge-neutral";
    case "suspended": return "es-badge es-badge-error";
    default: return "es-badge es-badge-neutral";
  }
}

export default function UsersPage() {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([api.get("/users"), api.get("/companies")]);
      setUsers(u.data); setCompanies(c.data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onRoleChange = async (id, role) => {
    try {
      const { data } = await api.patch(`/users/${id}`, { role });
      setUsers(us => us.map(u => u.id === id ? data : u));
      toast.success(`Role updated to ${role}.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); setUsers(us => us.filter(u => u.id !== id)); toast.success("User deleted."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const allColumns = [
    { key: "Name", label: "Name", cell: (u) => <span className="font-semibold">{u.full_name}</span> },
    { key: "Email", label: "Email", cell: (u) => <span className="font-mono text-xs">{u.email}</span> },
    {
      key: "Role", label: "Role",
      cell: (u) => (
        <div className="flex items-center gap-2">
          <span className={roleBadge(u.role)}>{u.role}</span>
          <select value={u.role} onChange={(e) => onRoleChange(u.id, e.target.value)} className="es-select h-8 text-xs">
            {ROLES.map((r) => <option key={r}>{r}</option>)}
          </select>
        </div>
      ),
    },
    { key: "Company", label: "Company", cell: (u) => <span className="text-xs">{companies.find((c) => c.id === u.Company_ID)?.Company_Name || "—"}</span> },
    { key: "Created", label: "Created", cell: (u) => <span className="font-mono text-xs">{u.created_date?.slice(0, 10)}</span> },
    { key: "Last_Login", label: "Last login", defaultVisible: false, cell: (u) => <span className="font-mono text-xs">{u.last_login?.slice(0, 16).replace("T", " ") || "—"}</span> },
    {
      key: "_actions", label: "Actions", locked: true, align: "right",
      cell: (u) => (
        <button onClick={() => onDelete(u.id)} className="text-zinc-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
      ),
    },
  ];

  const { visibleColumns, orderedColumns, isVisible, toggleColumn, moveColumn, reset } =
    useColumnPrefs("users", allColumns);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="es-overline text-brand-600 mb-2">// access control</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Users</h1>
          <p className="text-sm text-zinc-600 mt-1">{users.length} accounts</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <ColumnChooser
            tableLabel="users"
            orderedColumns={orderedColumns}
            isVisible={isVisible}
            toggleColumn={toggleColumn}
            moveColumn={moveColumn}
            reset={reset}
          />
          <button data-testid={TESTIDS.newUserBtn} onClick={() => setShowNew(true)} className="es-btn-primary">
            <Plus className="w-4 h-4" /> New user
          </button>
        </div>
      </div>

      {err && <div className="es-card p-4 text-sm text-red-700">{err}</div>}

      <div className="es-card overflow-x-auto">
        <table className="es-table">
          <thead>
            <tr>
              {visibleColumns.map((c) => (
                <th key={c.key} className={[c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "", c.width || ""].join(" ")}>
                  {c.header ? c.header() : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={visibleColumns.length} className="text-center py-10 font-mono text-xs text-zinc-500">Loading…</td></tr>}
            {!loading && users.length === 0 && <tr><td colSpan={visibleColumns.length} className="text-center py-10 font-mono text-xs text-zinc-500">No users.</td></tr>}
            {users.map((u) => (
              <tr key={u.id} data-testid={TESTIDS.userRow(u.id)}>
                {visibleColumns.map((c) => (
                  <td key={c.key} className={c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : ""}>{c.cell(u)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && (
        <NewUserModal companies={companies}
          onClose={() => setShowNew(false)}
          onCreated={(u) => { setUsers(prev => [u, ...prev]); setShowNew(false); }} />
      )}
    </div>
  );
}

function NewUserModal({ companies, onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", password: "", full_name: "", role: "user", Company_ID: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      const payload = { ...form, Company_ID: form.Company_ID || null };
      const { data } = await api.post("/users", payload);
      onCreated(data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 bg-zinc-900/60 z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm border border-zinc-300 w-full max-w-md">
        <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
          <h3 className="font-display text-lg font-bold">New User</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
          <div><label className="es-label">Full name *</label><input required value={form.full_name} onChange={e => u("full_name", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Email *</label><input type="email" required value={form.email} onChange={e => u("email", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Password *</label><input type="password" required minLength={6} value={form.password} onChange={e => u("password", e.target.value)} className="es-input" /></div>
          <div>
            <label className="es-label">Role</label>
            <select value={form.role} onChange={e => u("role", e.target.value)} className="es-select w-full">
              {ROLES.map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="es-label">Company (optional)</label>
            <select value={form.Company_ID} onChange={e => u("Company_ID", e.target.value)} className="es-select w-full">
              <option value="">— none —</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.Company_Name}</option>)}
            </select>
          </div>

          {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-sm">{err}</div>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="es-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="es-btn-primary">{saving ? "Saving…" : "Create user"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
